import { Modal, Table, Text } from "@mantine/core";
import { type MouseEventHandler, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { generateHeadersRow } from "src/generator/utils/IlTableGenerateFunctions";
import { useGetApi, useTheme } from "src/hooks";
import { type ICol } from "src/interfaces";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setLogbook } from "src/redux/slices/generatorSlice";
import { StyledTd, StyledTh, StyledTr } from "src/styled";
import { v4 as uuidv4, v4 } from "uuid";

import useTriggerInlineSave from "../../hooks/useTriggerInlineSave";
import { setEditing } from "../../redux/slices/entrySlice";
import { setIlModalData } from "../../redux/slices/generatorFeaturesSlice";
import { deepClone } from "../../utils";
import { replaceExclusiveValues } from "src/generator/utils/dynamicRows/replaceExclusiveValues";
import { unregisterRowFormFields } from "../utils/collectActiveFormFieldNames";

const mapIlData = (data: any) => ({
  name: data.data.ilname,
  data: data.data.ildata,
});

const IlTableModal = () => {
  const theme = useTheme();
  const triggerSave = useTriggerInlineSave();
  const lbentry = useSelector(state => state.generatorSlice.lbentry);
  const { ilModalData } = useSelector(state => state.generatorFeaturesSlice);
  const dispatch = useDispatch();
  const { data, refetch } = useGetApi({
    request: "get-info-list",
    body: { params: { ilid: ilModalData?.il } },
    fetchOnInit: false,
    mapper: mapIlData,
  });
  const { setValue, getValues, unregister } = useFormContext();

  useEffect(() => {
    if (ilModalData) {
      void refetch();
    }
  }, [ilModalData, refetch]);

  const rowIndex2 = data?.data.row;

  const handleClose = () => {
    dispatch(setIlModalData(null));
  };

  const generateTitle = (title: string, colcount?: number) => (
    <thead>
      <StyledTh colSpan={colcount ?? 99}>{title}</StyledTh>
    </thead>
  );

  const renderTable = () => {
    if (!data?.data?.sections) return <></>;

    return data.data.sections.map((section: any, idx: number) => {
      return (
        <Table mt={idx > 0 ? 15 : 0} key={idx}>
          {section.title && generateTitle(section.title, section.colcount)}
          {generateHeadersRow(section.headers)}
          {generateRows(section.rows, rowIndex2)}
        </Table>
      );
    });
  };

  const findObjectIdThatHasContentProperty = (cols: any) => {
    for (const key in cols) {
      if (Object.prototype.hasOwnProperty.call(cols, key)) {
        if (cols[key].id === "or-row") {
          return key;
        }
      }
    }
  };

  const generateRows = (rows: any[], takeValueFromRowByIndex: number) => {
    const disabled = ilModalData?.disabled ?? false;
    const handleClick: MouseEventHandler<HTMLTableRowElement> = tr => {
      if (!ilModalData) return;
      const {
        sectionIndex,
        rowIndex,
        orCode,
        changeCellValue,
        handleNMEAChange,
        tag,
        id,
      } = { ...ilModalData };
      if (disabled) return;
      triggerSave(sectionIndex);

      const clickedTr =
        tr.currentTarget.querySelectorAll("td")[takeValueFromRowByIndex]
          ?.textContent;

      let tableHeaderValue: any = null;
      if (
        ilModalData?.orCode === "or-number" ||
        ilModalData?.orCode === "or-code"
      ) {
        const firstDeepLevelCols = lbentry.sections[sectionIndex].rows[rowIndex]
          ?.cols as any as Record<string, ICol>;

        const idOfObjectWithContent =
          findObjectIdThatHasContentProperty(firstDeepLevelCols);

        let objWithContent;
        if (idOfObjectWithContent)
          objWithContent = firstDeepLevelCols[idOfObjectWithContent];

        let cloneObjWithContent = structuredClone(objWithContent);

        const clickedTable = tr.currentTarget.closest("table");
        if (clickedTable)
          tableHeaderValue =
            clickedTable.querySelectorAll("thead th")[takeValueFromRowByIndex]
              .textContent;

        if (
          clickedTr &&
          !data?.data.content[clickedTr]?.cols &&
          !data?.data.content[clickedTr]?.use
        ) {
          return;
        }

        if (
          clickedTr &&
          cloneObjWithContent &&
          idOfObjectWithContent &&
          data?.data.content[clickedTr]?.cols
        ) {
          cloneObjWithContent = {
            ...cloneObjWithContent,
            content: {
              cols: data.data.content[clickedTr].cols,
              flex: "",
            },
          };

          let colsWithIds: Record<string, any> = {};

          const toUuid = (data: any, index?: number) => {
            if (Array.isArray(data)) {
              if (index === undefined) {
                data.forEach((row, i) => toUuid(row, i));
                return;
              }
              data.forEach((col, i) => {
                toUuid({ ...col, gridRow: index, gridCol: i }, index);
              });
              return;
            }

            const item = {
              gridRow: data.gridRow ?? index ?? 0,
              gridCol: data.gridCol ?? 0,
              ...data,
            };

            colsWithIds = {
              ...colsWithIds,
              [uuidv4()]: item,
            };
          };

          if (cloneObjWithContent?.content?.cols) {
            toUuid(cloneObjWithContent.content.cols);
            cloneObjWithContent.content.cols =
              replaceExclusiveValues(colsWithIds);
          }

          const additionalRows: any[] = [];

          const isSingleRow =
            lbentry.sections[sectionIndex].rows[rowIndex].isSingleRowInCat ||
            Object.values(
              lbentry.sections[sectionIndex].rows[rowIndex].cols as any,
            ).every((el: any) => el.isSingleRow);

          if (data?.data.content[clickedTr]?.use && !isSingleRow) {
            const rest = data?.data.content[clickedTr].use;

            const row: { cols: Record<string, ICol> } = deepClone(
              lbentry.sections[sectionIndex].rows[rowIndex],
            );

            const copyPrevVal = lbentry.sections[sectionIndex].copyPrevVal;

            if (typeof copyPrevVal === "number") {
              const fieldIds = Object.keys(row.cols);
              let value = getValues(fieldIds[copyPrevVal]);

              if (value && row.cols[fieldIds[copyPrevVal]].unit === "date") {
                value = String(value);
              }

              row.cols[fieldIds[copyPrevVal]] = {
                ...row.cols[fieldIds[copyPrevVal]],
                value,
              };
            }

            rest.forEach((n: string) => {
              const content = data.data.content[n]?.cols;
              if (!content) return;
              const newRow: any = { cols: {} };

              Object.values(row.cols).forEach((value: any) => {
                let newValue = value.value;

                if (value.id === "or-code") newValue = tableHeaderValue;
                else if (value.id === "or-number") newValue = n;

                const newCol: any = { ...value, value: newValue };

                if (newCol.id === "or-row") {
                  colsWithIds = {};
                  toUuid(content);
                  newCol.content = {
                    cols: replaceExclusiveValues(colsWithIds),
                    flex: "",
                  };
                }

                newRow.cols[v4()] = newCol;
              });

              additionalRows.push(newRow);
            });
          }

          const sections = deepClone(lbentry.sections);

          if (additionalRows.length > 0) {
            unregisterRowFormFields(
              getValues(),
              lbentry.sections[sectionIndex].rows[rowIndex],
              unregister,
            );
            sections[sectionIndex].rows.splice(rowIndex, 1, ...additionalRows);
          } else {
            if (idOfObjectWithContent && objWithContent) {
              unregisterRowFormFields(
                getValues(),
                { cols: { [idOfObjectWithContent]: objWithContent } },
                unregister,
              );
            }

            const row = structuredClone({
              ...sections[sectionIndex].rows[rowIndex],
              cols: {
                ...sections[sectionIndex].rows[rowIndex].cols,
                [idOfObjectWithContent]: cloneObjWithContent,
              },
            });

            const entries = Object.entries(row.cols);

            entries.forEach(([key, value]: [string, any]) => {
              row.cols[key].isSingleRow = true;

              if (value.id === "or-code") {
                row.cols[key].value = tableHeaderValue;
                setValue(key, tableHeaderValue);
              } else if (value.id === "or-number") {
                row.cols[key].value = clickedTr;
                setValue(key, clickedTr);
              }
            });

            sections[sectionIndex].rows.splice(rowIndex, 1, row);
          }

          dispatch(setLogbook(sections));
        }
      } else {
        if (changeCellValue && tag === "actcode") {
          const secondValue =
            tr.currentTarget.querySelectorAll("td")[1].textContent;
          changeCellValue(secondValue as string);
        }

        handleNMEAChange?.(clickedTr ?? "");
        setValue(id, clickedTr);
      }

      dispatch(setEditing(false));
      setTimeout(() => dispatch(setEditing(true)), 0);
      handleClose();
    };

    const generatedRows = rows.map(row => {
      let nosel = false;
      const generatedCells = row.map((cell: any, k: number) => {
        nosel = !nosel ? cell?.nosel : nosel;
        let header = null;
        let width = "";
        if (ilModalData?.sectionIndex && data?.data?.sections) {
          header = data?.data.sections[ilModalData.sectionIndex]?.headers[k];
          if (header) {
            width = header.width ? header.width + "px" : "";
          }
        }
        const italic = cell?.italic ? "italic" : "";
        const selectable =
          cell?.selectable === false || cell?.nosel === true ? "none" : "auto";
        return (
          <StyledTd key={uuidv4()} width={width} selectable={selectable}>
            <Text fs={italic} style={{ width: width }}>
              {cell?.name}
            </Text>
          </StyledTd>
        );
      });
      if (!data) {
        return <></>;
      }
      return (
        <StyledTr
          hover={!disabled && !nosel}
          pointer={!disabled && !nosel}
          onClick={handleClick}
          key={uuidv4()}>
          {generatedCells}
        </StyledTr>
      );
    });

    return <tbody>{generatedRows}</tbody>;
  };

  return (
    <Modal
      yOffset={50}
      size={"50%"}
      opened={ilModalData !== null}
      zIndex={11}
      onClose={handleClose}
      title={data?.name ?? ""}>
      {ilModalData?.sectionIndex &&
      ilModalData?.rowIndex &&
      lbentry.sections[ilModalData.sectionIndex]?.rows[ilModalData.rowIndex]
        ?.isSingleRowInCat ? (
        <b
          style={{
            color: theme.colors?.main ? theme.colors.main[9] : "red",
            fontWeight: 600,
          }}>
          WILL BE ADDED AS SINGLE ROW
        </b>
      ) : (
        ""
      )}
      {renderTable()}
    </Modal>
  );
};

export default IlTableModal;
